import React, { KeyboardEvent, useRef, useState } from 'react';

import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';

import { OhsHrLookup } from 'human-resource/models/OhsHumanResourceModels';
import { OhsFormFieldBaseProps } from 'global-components/form/services/OhsFormModels';
import { ModuleType } from 'global-services/constants/OhsObject';
import DragAndDropSelectItem from 'global-components/dnd/OhsDragAndDropDisplay';
import { replaceCountryCodeByName } from 'global-services/OhsDataParse';
import OhsContractorLookup from 'contractor/models/OhsContractorLookUp';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import OhsFormFieldLayout from '../../components/OhsFormFieldLayout';
import { SelectFieldOptionType } from '../OhsSelectField';
import { OhsUser } from '../../../../user/OhsUser';
import SearchResultMenu from './SearchResultMenu';
import OhsHiddenField from '../../components/OhsHiddenField';
import OhsDragAndDrop from '../../../dnd/OhsDragAndDrop';
import { returnUniqueLink } from './OhsHrContractorLinkServices';
import { returnFieldStyle, returnVariant } from '../../components/OhsFieldLayout';

const MaxResults = 10;

const defaultProps = {
  maxCount: undefined,
};

export const LinkTypeSelectOptions = (user?: OhsUser): SelectFieldOptionType[] => [
  { name: 'Link to All', value: 'ALL' },
  { name: `Link to ${user?.configs.hr?.TITLE ?? ''}`, value: ModuleType.HR },
  {
    name: `Link to ${user?.configs.contractor?.TITLE ?? ''}`,
    value: ModuleType.Contractor,
  },
];

export interface Props extends OhsFormFieldBaseProps {
  ohsHrLookupList: OhsHrLookup[];
  ohsContractorList: OhsContractorLookup[];
  maxCount?: number;
}
function InputEle(props: Props) {
  const user = OhsUserLocalServices.getLocalOhsUser();
  const links = (props.ohsFormSettings.useFormMethods.watch(props.id) ?? []) as (
    | OhsHrLookup
    | OhsContractorLookup
  )[];

  const handleArrange = (list: string[]) => {
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...list]);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = React.useState('');

  const [ohsHrLookupResults, setOhsHrLookResults] = React.useState(new Array<OhsHrLookup>());

  const [ohsContractorResults, setOhsContractorResults] = React.useState(
    new Array<OhsContractorLookup>()
  );

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayRenge, setDisplayRange] = useState({ top: -1, bottom: -1 });
  const paperRef = useRef<any>();
  const keyTypes = { arrowDown: 'ArrowDown', arrowUp: 'ArrowUp', enter: 'Enter' };

  const handleSearchChange = (event: any) => {
    const value = (event.target.value as string) ?? '';
    if (value && value.length > 1) {
      const words = value.split(' ');
      let counter = 0;
      const newHrResults = new Array<OhsHrLookup>();
      const newContractorResults = new Array<OhsContractorLookup>();
      for (let index = 0; index < props.ohsHrLookupList.length; index += 1) {
        const hr = props.ohsHrLookupList[index];
        let found = true;
        words.forEach((word) => {
          if (JSON.stringify(hr).toLocaleLowerCase().indexOf(word.toLocaleLowerCase()) < 0) {
            found = false;
          }
        });
        if (found === true) {
          newHrResults.push(hr);
          counter += 1;
        }

        if (counter === MaxResults) break;
      }
      for (let index = 0; index < props.ohsContractorList.length; index += 1) {
        if (counter === MaxResults) break;
        const contractor = props.ohsContractorList[index];
        let found = true;
        words.forEach((word) => {
          if (
            JSON.stringify(contractor).toLocaleLowerCase().indexOf(word.toLocaleLowerCase()) < 0
          ) {
            found = false;
          }
        });
        if (found === true) {
          newContractorResults.push(contractor);
          counter += 1;
        }
      }

      const sortLinks = (type: ModuleType.HR | ModuleType.Contractor, result: any) => {
        const linkByType = (links || []).filter((link) => link.type === type);
        const linksWithSort = linkByType.map((link: OhsHrLookup | OhsContractorLookup) => ({
          ...link,
          sortingLabel: returnUniqueLink(link),
        }));
        const newLinksWithSort = result.map((link: OhsHrLookup | OhsContractorLookup) => ({
          ...link,
          sortingLabel: returnUniqueLink(link),
        }));
        _.pullAllBy(newLinksWithSort, linksWithSort, 'sortingLabel');
        const finalResult = newLinksWithSort.map((link: OhsHrLookup | OhsContractorLookup) => {
          return _.omit(link, ['sortingLabel']);
        });

        return finalResult;
      };

      // Hide options that are already selected
      setOhsHrLookResults(sortLinks(ModuleType.HR, newHrResults));
      setOhsContractorResults(sortLinks(ModuleType.Contractor, newContractorResults));

      setAnchorEl(event.currentTarget);
    }
    setSearchText(value);
  };
  const handleAddLink = (link: OhsHrLookup | OhsContractorLookup) => {
    if (_.has(link, 'moduleName')) _.unset(link, 'moduleName');
    if (_.has(link, 'workplaceId')) _.unset(link, 'workplaceId');
    if (_.has(link, 'position')) _.unset(link, 'position');
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...links, link]);
    props.ohsFormSettings.useFormMethods.trigger(props.id);
    setSearchText('');
  };
  const handleDeleteLink = (link: OhsHrLookup | OhsContractorLookup) => {
    props.ohsFormSettings.useFormMethods.setValue(props.id, [
      ...links.filter((item) => returnUniqueLink(item) !== returnUniqueLink(link)),
    ]);
    props.ohsFormSettings.useFormMethods.trigger(props.id);
  };

  const returnLinkData = (link: OhsHrLookup | OhsContractorLookup) => {
    let moduleName = '';
    let linkDetails = '';
    let email = '';
    switch (link.type) {
      case ModuleType.HR: {
        moduleName = user?.configs.hr?.TITLE ?? '';
        linkDetails = link.name;
        email = link?.email || '';
        break;
      }
      case ModuleType.Contractor: {
        moduleName = user?.configs.contractor?.TITLE ?? '';
        linkDetails = link.businessName;
        email = link.contactEmail || '';
        if (link.contactName) {
          linkDetails += ` (${link.contactName})`;
        }
        break;
      }
      default: {
        moduleName = '';
        break;
      }
    }

    return {
      label: `${moduleName} ${linkDetails}`,
      description: email,
    };
  };

  React.useEffect(() => {
    props.ohsFormSettings.useFormMethods.register(props.id, {
      validate: {
        notEmpty: () => {
          const value = props.ohsFormSettings.useFormMethods.getValues(props.id) ?? [];
          if (props.required === true) {
            if (value && value.length > 0) {
              return true;
            }
          } else {
            return true;
          }

          return false;
        },
        noDuplicated: () => {
          const linkList = props.ohsFormSettings.useFormMethods.getValues(props.id) ?? [];

          return !(linkList || []).some((link: OhsHrLookup | OhsContractorLookup, index: number) =>
            linkList
              .slice(index + 1)
              .some((newLink: OhsHrLookup | OhsContractorLookup) => newLink._id === link._id)
          );
        },
      },
    });
    const linkValues = props.ohsFormSettings.useFormMethods.getValues(props.id);
    const parsedLinkValues = (linkValues || []).map((link: OhsHrLookup | OhsContractorLookup) => {
      if (link.type === ModuleType.HR) {
        return {
          ...link,
          businessName: replaceCountryCodeByName(link.businessName),
        };
      }
      return link;
    });
    props.ohsFormSettings.useFormMethods.setValue(props.id, parsedLinkValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.required]);

  const handleMenuClose = () => {
    setHighlightedIndex(-1);
    setDisplayRange({ top: -1, bottom: -1 });
    paperRef.current?.scrollTo(0, 0);
    setAnchorEl(null);
  };

  const handleManualScroll = (keyType: string, index: number) => {
    const itemHeight = 84.95;
    if (paperRef.current) {
      const { offsetHeight } = paperRef.current;
      const size = Math.floor(offsetHeight / itemHeight);
      if (keyType === keyTypes.arrowDown) {
        setDisplayRange((pre) => ({ ...pre, bottom: pre.bottom + 1 }));
        if (index >= size) {
          setDisplayRange((pre) => ({ ...pre, top: pre.top + 1 }));
          paperRef.current.scrollBy(0, itemHeight);
        }
      }
      if (keyType === keyTypes.arrowUp) {
        setDisplayRange((pre) => ({ ...pre, bottom: pre.bottom - 1 }));
        if (index >= 0 && displayRenge.bottom <= displayRenge.top) {
          setDisplayRange((pre) => ({ ...pre, top: pre.top - 1 }));
          paperRef.current.scrollBy(0, -itemHeight);
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const lookupList = [...ohsHrLookupResults, ...ohsContractorResults];
    if (event.key === keyTypes.arrowDown && highlightedIndex <= lookupList.length - 2) {
      setHighlightedIndex((prev) => prev + 1);
      handleManualScroll(keyTypes.arrowDown, highlightedIndex + 1);
    } else if (event.key === keyTypes.arrowUp && highlightedIndex > 0) {
      setHighlightedIndex((prev) => prev - 1);
      handleManualScroll(keyTypes.arrowUp, highlightedIndex - 1);
    } else if (
      event.key === keyTypes.enter &&
      highlightedIndex > -1 &&
      highlightedIndex < lookupList.length
    ) {
      const item: any = lookupList.find((temp, index) => index === highlightedIndex);
      handleAddLink(item);
      handleMenuClose();
    } else if (event.key === 'Escape') handleMenuClose();
  };

  const handleRefFetch = (ref: any) => {
    if (ref && ref.current) {
      paperRef.current = ref.current;
    }
  };
  const limitReached = !!(props.maxCount && props.maxCount <= (links || []).length);

  return (
    <>
      <OhsHiddenField register={props.ohsFormSettings.useFormMethods.register(props.id)} />

      {!props.disabled && (
        <TextField
          type="text"
          fullWidth
          margin="dense"
          placeholder="Search..."
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
          onChange={handleSearchChange}
          onKeyDown={(event: KeyboardEvent) => {
            handleKeyDown(event);
          }}
          variant={returnVariant(props.ohsFormSettings.layout)}
          disabled={props.disabled || limitReached}
          value={searchText}
          style={returnFieldStyle(props.ohsFormSettings.layout)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
            autoComplete: 'off',
          }}
        />
      )}
      <SearchResultMenu
        anchorEl={anchorEl}
        handleMenuClose={handleMenuClose}
        ohsHrLookupList={ohsHrLookupResults}
        ohsContractorList={ohsContractorResults}
        setAnchorEl={setAnchorEl}
        handleAddLink={handleAddLink}
        hoveredItem={highlightedIndex}
        handleRefFetch={handleRefFetch}
        handleKeyDownFunc={handleKeyDown}
      />
      <div style={{ marginTop: '5px' }}>
        <OhsDragAndDrop
          list={links || []}
          setList={handleArrange}
          withHandle
          renderItem={(item: OhsHrLookup | OhsContractorLookup) => {
            const targetLink = returnLinkData(item);
            return (
              <DragAndDropSelectItem
                label={targetLink?.label}
                description={targetLink?.description}
                onDelete={() => handleDeleteLink(item)}
                disabled={props.disabled}
              />
            );
          }}
        />
      </div>
    </>
  );
}

InputEle.defaultProps = defaultProps;

export default function OhsLinkHrContractorField(props: Props) {
  return <OhsFormFieldLayout {...props} inputEle={<InputEle {...props} />} />;
}

OhsLinkHrContractorField.defaultProps = defaultProps;
